import { useState, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, ImagesService } from '@/api';
import { errorToastText } from '@/lib/translateBackendError';
import type { images_GenerateUploadURLRequest } from '@/types/image';
import { validateFile } from '@/config/storage';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';

// Custom error class for upload cancellation
class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadCancelledError';
  }
}

export interface UploadProgress {
  status: 'idle' | 'generating' | 'uploading' | 'confirming' | 'complete' | 'error' | 'cancelled';
  progress: number;
  error?: string;
}

export interface UseImageUploadOptions {
  purpose: images_GenerateUploadURLRequest.purpose;
  entityType?: string;
  entityId?: string;
  onSuccess?: (imageId: string) => void;
  onError?: (error: Error) => void;
  /** No success toast: a caller that uploads several files reports once. */
  silent?: boolean;
}

/**
 * Hook for 3-step image upload process:
 * 1. Generate presigned upload URL
 * 2. Upload file to storage
 * 3. Confirm upload completion
 */
export function useImageUpload({
  purpose,
  entityType,
  entityId,
  onSuccess,
  onError,
  silent = false,
}: UseImageUploadOptions) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: 'idle',
    progress: 0,
  });
  
  // Store abort controller and XMLHttpRequest for current upload
  const uploadAbortController = useRef<AbortController | null>(null);
  const currentXhr = useRef<XMLHttpRequest | null>(null);

  // Step 1: Generate upload URL
  const generateUrlMutation = useMutation({
    mutationFn: async (request: images_GenerateUploadURLRequest) => {
      return ImagesService.postApiV1ImagesUploadUrl(request);
    },
  });

  // Step 3: Confirm upload
  const confirmUploadMutation = useMutation({
    mutationFn: async (imageId: string) => {
      return ImagesService.postApiV1ImagesConfirm(imageId);
    },
    onSuccess: () => {
      // Invalidate image queries
      queryClient.invalidateQueries({ queryKey: ['images'] });
      if (entityType && entityId) {
        queryClient.invalidateQueries({ 
          queryKey: ['images', entityType, entityId] 
        });
      }
    },
  });

  const uploadFile = useCallback(async (file: File, description?: string) => {
    // Clean up any existing upload
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    if (currentXhr.current) {
      currentXhr.current.abort();
      currentXhr.current = null;
    }
    
    // Validate file
    const validation = validateFile(file, purpose);
    if (!validation.valid) {
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: validation.error,
      });
      toast.error(validation.error);
      onError?.(new Error(validation.error));
      return;
    }

    // Create new abort controller for this upload
    const abortController = new AbortController();
    uploadAbortController.current = abortController;

    try {
      // Step 1: Generate upload URL
      setUploadProgress({ status: 'generating', progress: 10 });
      
      let urlResponse;
      try {
        urlResponse = await generateUrlMutation.mutateAsync({
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          purpose,
          entityType,
          entityId,
          description,
        });
      } catch (generateError: unknown) {
        // Check for rate limit error
        if (generateError && typeof generateError === 'object' && 'response' in generateError) {
          const errorWithResponse = generateError as { response?: { status?: number } };
          if (errorWithResponse.response?.status === 429) {
            throw new Error(i18n._(t`Upload rate limit exceeded. Please try again in a minute.`));
          }
        }
        throw generateError;
      }

      if (!urlResponse.uploadUrl || !urlResponse.imageId) {
        throw new Error(i18n._(t`Failed to generate upload URL`));
      }

      // Check if cancelled during URL generation
      if (abortController.signal.aborted) {
        throw new UploadCancelledError();
      }

      // Step 2: Upload file to storage
      setUploadProgress({ status: 'uploading', progress: 30 });

      const xhr = new XMLHttpRequest();
      currentXhr.current = xhr;
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 60) + 30;
          setUploadProgress({ 
            status: 'uploading', 
            progress: percentComplete 
          });
        }
      });

      // Create promise for upload
      const uploadPromise = new Promise<void>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(i18n._(t`Upload failed`)));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error(i18n._(t`Upload failed`)));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new UploadCancelledError());
        });
      });

      // Listen for abort signal
      const handleAbort = () => {
        xhr.abort();
      };
      abortController.signal.addEventListener('abort', handleAbort);

      // Start upload
      xhr.open('PUT', urlResponse.uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

      await uploadPromise;

      // Check if cancelled during upload
      if (abortController.signal.aborted) {
        throw new UploadCancelledError();
      }

      // Step 3: Confirm upload
      setUploadProgress({ status: 'confirming', progress: 90 });
      
      await confirmUploadMutation.mutateAsync(urlResponse.imageId);

      // Success
      setUploadProgress({ status: 'complete', progress: 100 });
      if (!silent) {
        toast.success(i18n._(t`Image uploaded successfully`));
      }
      onSuccess?.(urlResponse.imageId);

      // Clean up controllers on success
      uploadAbortController.current = null;
      currentXhr.current = null;

      // Reset after a delay
      setTimeout(() => {
        setUploadProgress({ status: 'idle', progress: 0 });
      }, 2000);

    } catch (error) {
      // Clean up controllers on error
      uploadAbortController.current = null;
      currentXhr.current = null;
      
      // Handle cancellation separately
      if (error instanceof UploadCancelledError) {
        setUploadProgress({
          status: 'cancelled',
          progress: 0,
          error: i18n._(t`Upload cancelled`),
        });
        toast.info(i18n._(t`Upload cancelled`));
        return;
      }
      
      // Errors raised here carry translated text; an API error goes through the
      // backend-error mapping so a raw status text never reaches the user.
      const fallback = i18n._(t`Upload failed`);
      const errorMessage =
        error instanceof ApiError || !(error instanceof Error)
          ? errorToastText(error, i18n, fallback)
          : error.message;
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: errorMessage,
      });
      toast.error(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    }
  }, [
    purpose, 
    entityType, 
    entityId, 
    generateUrlMutation, 
    confirmUploadMutation, 
    onSuccess, 
    onError,
    silent,
    i18n
  ]);

  const cancelUpload = useCallback(() => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    if (currentXhr.current) {
      currentXhr.current.abort();
      currentXhr.current = null;
    }
    setUploadProgress({
      status: 'cancelled',
      progress: 0,
      error: i18n._(t`Upload cancelled`),
    });
  }, [i18n]);

  const reset = useCallback(() => {
    setUploadProgress({ status: 'idle', progress: 0 });
    uploadAbortController.current = null;
    currentXhr.current = null;
  }, []);

  return {
    uploadFile,
    uploadProgress,
    isUploading: uploadProgress.status !== 'idle' && uploadProgress.status !== 'complete' && uploadProgress.status !== 'error' && uploadProgress.status !== 'cancelled',
    reset,
    cancelUpload,
  };
}